import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import useMobileBlurOnOrientationChanged from "hooks/useMobileBlurOnOrientationChanged";

interface Data {
  value: string;
  rows: number;
  maxRows: number;
  rowHeight: number;
  autoGrow: boolean;
  showLengthCounter: boolean;
  setTextarea?: (textArea: HTMLTextAreaElement | null) => void;
}

export default function useViewController({
  value,
  rows,
  rowHeight,
  autoGrow,
  maxRows,
  showLengthCounter,
  setTextarea,
}: Data) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [contentHeight, setContentHeight] = useState(rows * rowHeight);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [paddingY, setPaddingY] = useState(0);

  const minHeight = useMemo(() => rows * rowHeight, [rows, rowHeight]);
  const maxHeight = useMemo(() => Math.max(maxRows * rowHeight, minHeight), [maxRows, rowHeight, minHeight]);
  const currentHeight = useMemo(() => {
    const defaultHeight = rows * rowHeight;
    if (!autoGrow) return defaultHeight;
    return Math.min(contentHeight, maxHeight);
  }, [autoGrow, contentHeight, maxHeight, rowHeight, rows]);
  const count = useMemo(() => {
    if (!showLengthCounter) return 0;
    return value.length;
  }, [showLengthCounter, value]);

  // Measure the textarea's natural content height WITHOUT ever touching its
  // `.value`. The previous code wrote `el.value` to measure scrollHeight, which
  // on Android aborts the in-flight IME / autocorrect-suggestion composition and
  // drops typed characters. We only toggle `style.height` (layout-only, so it is
  // composition-safe): reset to 0 so scrollHeight reflects true content (which
  // also lets the box shrink on delete), read, then restore.
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const style = getComputedStyle(el);
    const pad = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    setPaddingY((p) => (p !== pad ? pad : p));
    if (!autoGrow) return;
    const prev = el.style.height;
    el.style.height = "0px";
    const next = el.scrollHeight - pad;
    el.style.height = prev;
    if (Number.isFinite(next)) setContentHeight(next > 0 ? next : rows * rowHeight);
  }, [autoGrow, rows, rowHeight]);

  // Programmatic value changes (emoji insert, draft restore, @mention, format
  // toolbar, shared-content append) and non-composition typing. useLayoutEffect
  // so the height is corrected before paint — no one-frame flicker / late growth.
  useLayoutEffect(() => {
    measure();
  }, [value, measure]);

  // Live typing path. On Android the soft keyboard keeps the current word in a
  // composition / suggestion state until it is committed (on space), so the React
  // `value` round-trip can lag the wrapping keystroke. The native `input` event
  // fires for every keystroke (incl. mid-composition) and `compositionend` fires
  // on commit — re-measuring on both makes the field grow on the keystroke that
  // wraps, not one space later. rAF-coalesced to absorb fast / swipe typing.
  useEffect(() => {
    const el = ref.current;
    if (!el || !autoGrow) return;
    let raf = 0;
    const schedule = () => {
      if (typeof requestAnimationFrame !== "function") {
        measure();
        return;
      }
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    el.addEventListener("input", schedule);
    el.addEventListener("compositionend", schedule);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("input", schedule);
      el.removeEventListener("compositionend", schedule);
    };
  }, [autoGrow, measure]);

  useEffect(() => {
    if (ref.current && setTextarea) {
      setTextarea(ref.current);
      setScrollbarWidth(ref.current.offsetWidth - ref.current.clientWidth);
    }
  }, [setTextarea]);

  useMobileBlurOnOrientationChanged(ref);

  return {
    ref,
    scrollbarWidth,
    minHeight,
    maxHeight,
    currentHeight,
    count,
    paddingY,
  };
}
